
/// <amd-module name="Build/DesignerExtensions/RequiredTemplateInfoPanel/RequiredTemplateInfoPanel" />
import { TemplateConstants, TemplateParameterTypes } from 'Build/Designer/Constants';
import * as TaskAgent from "DistributedTask/Client/Contracts/TaskAgent";
import * as Resources from 'Build/Designer/Resources';
import { TemplateInfo, TemplateParameterDefinition } from 'Build/Designer/Wizard.types';
import { AzureContainerRegistryPicker } from 'Build/DesignerExtensions/YamlSnippetForm/Components/AzureContainerRegistryPicker';
import { AzureKubernetesEndpointInput } from 'Build/DesignerExtensions/YamlSnippetForm/Components/AzureKubernetesEndpointInput';
import { DataSourcePickList } from "Build/DesignerExtensions/YamlSnippetForm/Components/DataSourcePickList";
import { EndpointHelper } from "Build/DesignerExtensions/YamlSnippetForm/Utilities/EndpointHelper";
import { ISubscriptionContextValue, Subscription, SubscriptionConsumer, SubscriptionPicker, SubscriptionProvider } from 'DistributedTask/Subscriptions/SubscriptionPicker';
import * as React from 'react';
import { Components, IVssComponentContext, VssComponent } from 'VSS/Platform/Layout';
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IButtonProps } from 'VSSUI/Button';
import { ContentSize } from 'VSSUI/Callout';
import { ComboBox, IComboBoxOption } from "VSSUI/ComboBox";
import { TextField } from 'VSSUI/Components/TextField/TextField';
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { Panel } from 'VSSUI/Panel';
import { SnippetFormSource } from "Build/DesignerExtensions/YamlSnippetForm/Sources/SnippetFormSource";
import { YamlSnippetInput } from "Pipelines/Common/Library/Contracts/DataProvider";
import { MarkdownRendererComponent } from "VSS/Features/Markdown/MarkdownRendererComponent";

export interface Props {
    template: TemplateInfo;
    requires: TemplateParameterDefinition[];
    isOpen: boolean;
    onRequiredTemplateInfoCompleted: (requiredInfo: { [key: string]: object }) => void;
    onDismiss: () => void;
    onError: (error: any) => void;
}

export interface State {
    requiredInfo: { [key: string]: any };
    subscription?: Subscription;
    step: number;
    disablePrimaryButton: boolean;
    endpointAccessToken?: string;
}

export class RequiredTemplateInfoPanel extends VssComponent<Props, State, IVssComponentContext> {

    private readonly _renderTypes: Set<string> = new Set([
        TemplateParameterTypes.AzureContainerRegistry.toLocaleLowerCase(),
        TemplateParameterTypes.KubernetesResource.toLocaleLowerCase(),
        TemplateParameterTypes.PickList.toLocaleLowerCase(),
        TemplateParameterTypes.String.toLocaleLowerCase()
    ]);

    private _source: SnippetFormSource;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);
        const inputDefaults: any = {};
        this.props.requires.forEach((requiredInput) => {
            if (requiredInput.defaultValue) {
                inputDefaults[requiredInput.name] = requiredInput.defaultValue;
            }
        });
        this.state = {
            requiredInfo: inputDefaults,
            step: this._requiresSubscriptionInfo() ? 0 : 1,
            disablePrimaryButton: false
        };

        this._source = new SnippetFormSource(this.context.pageContext);
    }

    public componentDidUpdate(prevProps: Props, prevState: State, snapshot?: IVssComponentContext): void {
        super.componentDidUpdate(prevProps, prevState, snapshot);
    }

    public render(): JSX.Element {
        return (
            <div>
                {this.props.isOpen &&
                    <Panel
                        size={ContentSize.Medium}
                        onDismiss={this.props.onDismiss}
                        titleProps={{ text: this.props.template.name, className: "panel-title" }}
                        description={this.props.template.description}
                        footerButtonProps={this._getFooterButtons()}>
                        {this._renderBody()}
                    </Panel>
                }
            </div>
        );
    }

    private _renderBody(): JSX.Element {
        return (
            <div style={{ "width": "100%" }}>
                {this.state.step == 0 &&
                    <div className="ci-designer-required-info-select-panel-body flex-column flex-grow">
                        <SubscriptionProvider
                            pageContext={this.context.pageContext} >
                            <SubscriptionConsumer>
                                {
                                    (subscriptionContext: ISubscriptionContextValue) =>
                                        <SubscriptionPicker
                                            label={Resources.SelectAzureSubscription}
                                            subscriptionContext={subscriptionContext}
                                            onSubscriptionChoiceChanged={this._onSubscriptionChanged}
                                            selectedSubscriptionId={this._getSubscriptionSelectedKey()}
                                            className="default-margin-top body-m"
                                            titleClassName="body-m"
                                            radioButtonGroupSubtextClassName="radio-button-group-subtext"
                                        />}
                            </SubscriptionConsumer>
                        </SubscriptionProvider>
                    </div>
                }
                {
                    this.state.step == 1 &&
                    this._getComponentsToRender()
                }
            </div>
        );
    }

    private _getFooterButtons() {
        let footerButtons: IButtonProps[] = [];

        if (this.state.step == 0) {
            footerButtons = [{
                className: "primary-action-button",
                disabled: this.state.disablePrimaryButton || !this.state.subscription,
                onClick: this._onContinueClick,
                primary: true,
                text: Resources.Continue
            }];
        }
        else if (this.state.step == 1) {
            if (this._requiresSubscriptionInfo()) {
                footerButtons.push({
                    onClick: this._onBackClick,
                    text: "Back"
                });
            }

            footerButtons.push({
                className: "primary-action-button",
                disabled: this.state.disablePrimaryButton || !this._receivedAllInputs(),
                onClick: this._onValidateAndContinue,
                primary: true,
                text: Resources.ValidateAndConfigureButtonLabel
            });
        }

        return footerButtons;
    }

    private _getSubscriptionSelectedKey() {
        if (this.state.subscription) {
            return this.state.subscription.id;
        }
    }

    private _onContinueClick = async () => {
        const endpointToken = await EndpointHelper.getAuthorizationToken(this.context.pageContext, this.state.subscription);
        const info = this.state.requiredInfo;
        const azureRmEndpointInput = this.props.requires.find((requiredInput: TemplateParameterDefinition) => {
            return requiredInput.type == TemplateParameterTypes.AzureRmEndpoint;
        });

        if (azureRmEndpointInput) {
            info[azureRmEndpointInput.name] = EndpointHelper.getAzureRMServiceEndpointDetails(this.context.pageContext, this.state.subscription, endpointToken);
        }

        this.setState({
            step: 1,
            endpointAccessToken: endpointToken,
            requiredInfo: info
        });

    }

    private _requiresSubscriptionInfo(): boolean {
        return this.props.requires.some(input =>
            input.type == TemplateParameterTypes.AzureContainerRegistry
            || input.type == TemplateParameterTypes.KubernetesResource
            || input.type == TemplateParameterTypes.AzureRmEndpoint);
    }

    private _onBackClick = () => {
        this.setState({
            ...this.state,
            step: 0
        });
    }

    private _onSubscriptionChanged = (subscription: Subscription | undefined) => {
        if (subscription) {
            this._recordTelemetryInfo("SubscriptionSelected");
            this.setState({
                ...this.state,
                subscription: subscription
            })
        }
    }

    private _onValidateAndContinue = () => {
        const requiredInfo: any = this.state.requiredInfo;
        this._recordTelemetryInfo("ValidateAndContinueClicked");

        const azureRmEndpointInput = this.props.requires.find((requiredInput: TemplateParameterDefinition) => {
            return requiredInput.type == TemplateParameterTypes.AzureRmEndpoint
        });

        // If the template requires an endpoint of type AzureRm, add the subscription
        // details to the requiredInfo, as no other input is required for the endpoint.
        if (azureRmEndpointInput) {
            const endpointObject = this._createAzureRmServiceEndpointObject();
            if (endpointObject) {
                requiredInfo[azureRmEndpointInput.name] = endpointObject;
            }
        }

        this.setState({
            ...this.state,
            disablePrimaryButton: true
        })
        this.props.onRequiredTemplateInfoCompleted(requiredInfo);
    }

    private _getComponentsToRender() {
        const components: any[] = [];
        this.props.requires.forEach((requiredInput: TemplateParameterDefinition) => {
            switch (requiredInput.type.toLowerCase()) {
                case TemplateParameterTypes.KubernetesResource.toLowerCase(): {
                    components.push(<AzureKubernetesEndpointInput
                        className="panel-component body-m"
                        subscription={this.state.subscription}
                        endpointToken={this.state.endpointAccessToken}
                        key={requiredInput.name}
                        onAllValuesReceived={(info) => this._onValueUpdated(requiredInput.name, info)}
                        clusterAdmin={this.getValue("reviewApp")}
                        onError={this.props.onError}
                    />);
                    break;
                }
                case TemplateParameterTypes.AzureContainerRegistry.toLowerCase(): {
                    if (this.state.subscription) {
                        components.push(<AzureContainerRegistryPicker
                            className="panel-component body-m"
                            subscription={this.state.subscription}
                            endpointToken={this.state.endpointAccessToken}
                            key={requiredInput.name}
                            onAllValuesReceived={(info) => this._onValueUpdated(requiredInput.name, info)}
                            onError={this.props.onError}
                        />);
                    }
                    break;
                }
                case TemplateParameterTypes.PickList.toLowerCase(): {
                    const possibleValues = requiredInput.possibleValues || [];
                    components.push(
                        <FormItem
                            key={requiredInput.name}
                            label={requiredInput.displayName}
                            className="panel-component body-m">
                            <ComboBox
                                options={possibleValues.map(this._getComboBoxOption)}
                                text={this.getValue(requiredInput.name)}
                                autoComplete={true}
                                allowFreeform={true}
                                onChanged={(option, index, value) => this._onComboBoxUpdated(requiredInput.name, option, index, value)}
                            />
                        </FormItem>
                    );
                    break;
                }
                case TemplateParameterTypes.DataSourcePickList.toLowerCase(): {
                    components.push(
                        <DataSourcePickList
                            inputDefinition={this._getInputDefinitionForDataSourcePicklist(requiredInput)}
                            serviceConnectionProvider={this._source}
                            onValueChanged={this._onValueUpdated}
                            getValue={this._getValueForDataSourcePicklist}
                            onError={this.props.onError}
                            serviceEndpointDetails={this._getEndpointDetailsForDataSourcePicklist(requiredInput)}
                            makeRecursiveDatasourceCalls={true} />
                    );
                    break;
                }
                case TemplateParameterTypes.String.toLowerCase(): {
                    components.push(
                        <TextField
                            className="panel-component body-m"
                            key={requiredInput.name}
                            label={requiredInput.displayName}
                            value={this.getValue(requiredInput.name)}
                            onChange={(e, value) => this._onTextUpdated(requiredInput.name, value)} />);
                    break;
                }
                case TemplateParameterTypes.Boolean: {
                    const value = this.getValue(requiredInput.name);
                    components.push(
                        <Checkbox
                            className="panel-component body-m"
                            key={requiredInput.name}
                            label={requiredInput.displayName}
                            checked={value === "true"}
                            onChange={(e, checked) => this._onValueUpdated(requiredInput.name, checked ? "true" : "false")} />);
                    break;
                }
                case TemplateParameterTypes.MarkDown: {
                    components.push(
                        <MarkdownRendererComponent
                            className="panel-component md-component"
                            rawContent={requiredInput.displayName}
                            
                            />);
                    break;
                }
            }
        });

        return components;
    }

    private _getComboBoxOption = (item: string): IComboBoxOption => {
        return {
            text: item,
            key: item
        };
    }

    private _onComboBoxUpdated = (key: string, option?: IComboBoxOption, index?: number, value?: string) => {
        const updatedValue = option ? option.text : value;
        if (updatedValue) {
            this._onTextUpdated(key, updatedValue);
        }
    }

    private _getValueForDataSourcePicklist = (name: string): string => {
        if (name === TemplateConstants.SubscriptionId) {
            // The DataSourcePicklist is passed a callback function getValue, which it calls to get the value of any input
            // In case of Azure based endpoints, where an endpoint is not yet created, we pass subscriptionId as the dataSourceDependency.
            // When a callback is made to get the value of dataSourceDependency, we want to return some value (since otherwise it would
            // assume that its in a bad state to make a call), but don't want to return a Guid, as the service endpoint proxy call
            // would treat it as a request through an existing endpoint, and would fail.
            return !!this.state.subscription && !!this.state.subscription.id ? "0" : "";
        }

        if (name && name.indexOf("$(") === 0) {
            name = name.substr(2, name.length - 3);
        }

        return this.getValue(name) || "";
    }

    private _getInputDefinitionForDataSourcePicklist = (requiredInput: TemplateParameterDefinition): YamlSnippetInput => {
        const inputDefinition = {
            dataSourceBinding: {},
            label: requiredInput.displayName,
            name: requiredInput.name
        } as YamlSnippetInput;

        if (this.props.template.dataSourceBindings) {
            const dataSourceBinding = this.props.template.dataSourceBindings.find((binding) => binding.target === requiredInput.name);

            if (dataSourceBinding) {
                inputDefinition.dataSourceBinding = {
                    dataSourceName: dataSourceBinding.dataSourceName,
                    parameters: dataSourceBinding.parameters,
                    resultTemplate: dataSourceBinding.resultTemplate
                } as TaskAgent.DataSourceBinding;

                if (this._isAzureSubscriptionBasedEndpoint(dataSourceBinding.endpointParameterName)) {
                    inputDefinition.dataSourceDependency = TemplateConstants.SubscriptionId;
                }
            }
        }

        return inputDefinition;
    }

    private _isAzureSubscriptionBasedEndpoint = (endpointParameterName: string): boolean => {
        const endpointInput = this.props.requires.find((input) => input.name === endpointParameterName);
        return !!endpointInput
            && (endpointInput.type == TemplateParameterTypes.AzureContainerRegistry
                || endpointInput.type == TemplateParameterTypes.AzureRmEndpoint
                || endpointInput.type == TemplateParameterTypes.KubernetesResource);
    }

    private _getEndpointDetailsForDataSourcePicklist = (requiredInput: TemplateParameterDefinition) => {
        if (this.props.template.dataSourceBindings) {
            const dataSourceBinding = this.props.template.dataSourceBindings.find((binding) => binding.target === requiredInput.name);
            if (dataSourceBinding && dataSourceBinding.endpointParameterName) {
                // If endpointParameterName is specified, return the endpoint details corresponding to
                // the endpoint parameter.
                const endpointInput = this.props.requires.find((input) => input.name === dataSourceBinding!.endpointParameterName);
                if (endpointInput) {
                    return this.getValue(endpointInput.name) || undefined;
                }
            }
        }

        return undefined;
    }

    private _onValueUpdated = (key: string, value: any) => {
        const info = this.state.requiredInfo;
        info[key] = value;
        this._recordTelemetryInfo("RequiredInfoValueUpdated", { key: key });
        this.setState({
            requiredInfo: info
        });
    }

    private _onTextUpdated = (key: string, value: string) => {
        const info = this.state.requiredInfo;
        const val = value.trim();
        if (val !== '' && val.length > 0) {
            info[key] = value;
            this._recordTelemetryInfo("RequiredInfoValueUpdated", { key: key });
            this.setState({
                requiredInfo: info
            });
        }
    }

    private _createAzureRmServiceEndpointObject = () => {
        if (!this.state.subscription) {
            return null;
        }

        let endpoint: any;
        endpoint = EndpointHelper.getAzureRMServiceEndpointDetails(this.context.pageContext, this.state.subscription, this.state.endpointAccessToken);
        delete endpoint.authorization.parameters.accessTokenType;
        endpoint.name = `${this.state.subscription.displayName} (${this.state.subscription.id})`;

        return endpoint;
    }

    private getValue = (key: string) => {
        return this.state.requiredInfo[key];
    }

    private _receivedAllInputs = () => {
        let allReceived = true;
        this._recordTelemetryInfo("ValidateAndContinueButtonEnabled");
        this.props.requires.forEach((requiredInput: TemplateParameterDefinition) => {
            if (this._renderTypes.has(requiredInput.type.toLocaleLowerCase()) && !this.state.requiredInfo[requiredInput.name]) {
                allReceived = false;
            }
        });
        return allReceived;
    }

    private _recordTelemetryInfo = (action: string, additionalTelemetry?: { [key: string]: any }): void => {
        const telemetryData: { [key: string]: any } = {
            action: action,
            ...additionalTelemetry
        };

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(
            "Build",
            "CreatePipeline",
            telemetryData);
    }
}

Components.add("designer-required-template-info-panel", RequiredTemplateInfoPanel);
